import { create } from 'zustand';
import { creditApi } from '../lib/services/credit/creditApi';
import type {
  CreditHistoryItem,
  CreditHistoryType,
  CreditOverview,
} from '../types/credit';

/**
 * Digital Staff credit billing state (overview + history) for the new-plan
 * (credit-billed) surfaces: Credit Usage page and the sidebar badge.
 */
interface CreditState {
  overview: CreditOverview | null;
  history: CreditHistoryItem[];
  /**
   * The (type, limit) query that produced `history`, or null before the first load. Lets the page
   * tell whether the rows on screen belong to its current filter / page size.
   */
  historyQuery: { type?: CreditHistoryType; limit: number } | null;
  loadingOverview: boolean;
  loadingHistory: boolean;
  error: string | null;

  fetchOverview: (userId: string) => Promise<void>;
  fetchHistory: (userId: string, type?: CreditHistoryType, limit?: number) => Promise<void>;
  reset: () => void;
}

/**
 * Monotonic id of the latest fetchHistory call. Only the newest request may write `history`, so a
 * slow response for a previous filter / limit can't overwrite the rows of a newer one.
 */
let historyRequestSeq = 0;

export const useCreditStore = create<CreditState>(set => ({
  overview: null,
  history: [],
  historyQuery: null,
  loadingOverview: false,
  loadingHistory: false,
  error: null,

  fetchOverview: async (userId: string) => {
    if (!userId) return;
    set({ loadingOverview: true, error: null });
    try {
      const overview = await creditApi.getOverview(userId);
      set({ overview, loadingOverview: false });
    } catch (e) {
      set({ loadingOverview: false, error: (e as Error)?.message ?? 'Failed to load credits' });
    }
  },

  fetchHistory: async (userId: string, type?: CreditHistoryType, limit = 50) => {
    if (!userId) return;
    const seq = ++historyRequestSeq;
    set({ loadingHistory: true });
    try {
      const history = await creditApi.getHistory(userId, type, limit);
      if (seq !== historyRequestSeq) return; // superseded by a newer request
      set({ history, historyQuery: { type, limit }, loadingHistory: false });
    } catch (e) {
      if (seq !== historyRequestSeq) return;
      set({ loadingHistory: false, error: (e as Error)?.message ?? 'Failed to load history' });
    }
  },

  reset: () => {
    historyRequestSeq++; // drop any in-flight history response
    set({ overview: null, history: [], historyQuery: null, loadingHistory: false, error: null });
  },
}));
